import type { DashboardTableQueryParams } from './dashboardTable.types'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type DashboardPeriod = 'day' | 'week' | 'month' | 'year' | 'ytd'

export type ARAgingBucketName = '0-30' | '31-60' | '61-90' | '90+'

export type ARAgingPayerType = 'insurance' | 'patient'

export interface PeriodComparisonMetric {
  current: number
  previous: number
  percentChange: number
}

export interface ARAgingBucket {
  bucket: ARAgingBucketName
  insuranceAR: number
  patientAR: number
  percentOfOverdueAR: number
  appointmentCount: number
}

export interface ARAgingTypeRow {
  payerType: ARAgingPayerType
  totalAR: number
  percentOfOverdueAR: number
  ninetyPlusPercent: number
}

export interface ARAgingDashboard {
  asOfDate: string
  previousDate: string
  period: DashboardPeriod
  totals: {
    totalAR: PeriodComparisonMetric
    insuranceAR: PeriodComparisonMetric
    patientAR: PeriodComparisonMetric
    overdueAR: PeriodComparisonMetric
  }
  buckets: ARAgingBucket[]
  arType: ARAgingTypeRow[]
}

export interface OutstandingBalancePatientRow {
  patientId: string
  name: string
  charges: number
  payments: number
  balance: number
}

export interface OutstandingBalanceDashboard {
  asOfDate: string
  previousDate: string
  period: DashboardPeriod
  totals: {
    totalBalance: PeriodComparisonMetric
    totalCharges: PeriodComparisonMetric
    totalPayments: PeriodComparisonMetric
    accountsWithBalance: PeriodComparisonMetric
  }
  buckets: ARAgingBucket[]
  patients: OutstandingBalancePatientRow[]
}

export interface ProductionByProcedureRow {
  category: string
  production: number
  total_percentage: number
  number_of_procedures: number
}

export interface ProductionByProcedureResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: ProductionByProcedureRow[]
}

export interface ProductionByProviderRow {
  provider: string
  credential: string | null
  total_procedures: number
  total_production: number
}

export interface ProductionByProviderResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: ProductionByProviderRow[]
}

export interface ProcedureLogSummary {
  proc_log_id: string
  proc_description: string
}

export interface HygieneVisitRow {
  patientId: string
  patientName: string
  appointmentId: string
  providerName: string
  visitDate: string
  procedure_logs: ProcedureLogSummary[]
  total: number
}

export interface HygieneProductionPeriodBucket {
  label: string
  value: number
}

export interface HygieneProductionResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  hygieneProduction: PeriodComparisonMetric
  productionPerHygieneVisit: PeriodComparisonMetric
  periodSummary: HygieneProductionPeriodBucket[]
  visits: HygieneVisitRow[]
}

export type DashboardPeriodParams = {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export type DashboardFinancePeriodParams = DashboardPeriodParams

export interface ProductionByDepartmentRow {
  period: string
  dentistProduction: number
  hygienistProduction: number
  totalProduction: number
  productionPerVisit: number
  dentistProductionShare: number
  hygienistProductionShare: number
}

export interface ProductionByDepartmentResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  dentistProduction: PeriodComparisonMetric
  hygienistProduction: PeriodComparisonMetric
  productionPerVisit: PeriodComparisonMetric
  dentistPeriodSummary: HygieneProductionPeriodBucket[]
  hygienistPeriodSummary: HygieneProductionPeriodBucket[]
  rows: ProductionByDepartmentRow[]
}

export interface ProductionPeriodBucket {
  label: string
  value: number
}

export interface ProductionSummary {
  grossProduction: PeriodComparisonMetric
  netProduction: PeriodComparisonMetric
  adjustmentPercentage: PeriodComparisonMetric
  productionRate: PeriodComparisonMetric
}

export interface ProductionByProviderTableRow {
  provider: string
  gross: number
  writeOff: number
  discount: number
  net: number
  appointmentCount: number
}

export interface ProductionByProcedureTableRow {
  procedure: string
  gross: number
  writeOff: number
  discount: number
  net: number
  appointmentCount: number
}

export interface ProductionByTimePeriodTableRow {
  timePeriod: string
  gross: number
  net: number
  adjustments: number
  averagePerAppointment: number
}

export interface ProductionDashboardResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: ProductionSummary
  productionPeriodSummary: ProductionPeriodBucket[]
  accountingPeriodSummary: ProductionPeriodBucket[]
  byProvider: ProductionByProviderTableRow[]
  byProcedure: ProductionByProcedureTableRow[]
  byTimePeriod: ProductionByTimePeriodTableRow[]
}

export const getProduction = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionDashboardResult> =>
  axios
    .get<
      ApiResponse<ProductionDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionDashboardResult> =>
  axios
    .get<
      ApiResponse<ProductionDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface PeriodBucket {
  label: string
  value: number
}

export interface CollectionSummary {
  grossCollection: PeriodComparisonMetric
  netCollection: PeriodComparisonMetric
  adjustmentRate: PeriodComparisonMetric
  collectionRate: PeriodComparisonMetric
  insuranceCollection: PeriodComparisonMetric
  patientCollection: PeriodComparisonMetric
  insuranceDaysToCollect: PeriodComparisonMetric
  patientDaysToCollect: PeriodComparisonMetric
}

export interface CollectionByTimePeriodRow {
  period: string
  grossCollection: number
  netCollection: number
}

export interface CollectionByPayerSplitRow {
  period: string
  insuranceCollection: number
  patientCollection: number
  total: number
  insuranceSharePercent: number
  patientSharePercent: number
}

export interface CollectionDashboardResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: CollectionSummary
  grossCollectionPeriodSummary: PeriodBucket[]
  netCollectionPeriodSummary: PeriodBucket[]
  insuranceCollectionPeriodSummary: PeriodBucket[]
  patientCollectionPeriodSummary: PeriodBucket[]
  byTimePeriod: CollectionByTimePeriodRow[]
  byPayerSplit: CollectionByPayerSplitRow[]
}

export const getCollection = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<CollectionDashboardResult> =>
  axios
    .get<
      ApiResponse<CollectionDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/collection`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getCollectionOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<CollectionDashboardResult> =>
  axios
    .get<
      ApiResponse<CollectionDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/collection/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface ClaimsSummary {
  underReview: PeriodComparisonMetric
  pending: PeriodComparisonMetric
  paid: PeriodComparisonMetric
  denied: PeriodComparisonMetric
}

export interface ClaimsByPeriodRow {
  period: string
  underReview: number
  pending: number
  paid: number
  denied: number
  paidOverSubmittedPercent: number
}

export interface ClaimsDashboardResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: ClaimsSummary
  byPeriod: ClaimsByPeriodRow[]
}

export const getClaims = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ClaimsDashboardResult> =>
  axios
    .get<
      ApiResponse<ClaimsDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/claims`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getClaimsOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ClaimsDashboardResult> =>
  axios
    .get<
      ApiResponse<ClaimsDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/claims/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface PracticeRevenueSummary {
  grossRevenue: PeriodComparisonMetric
  netCollection: PeriodComparisonMetric
  adjustment: PeriodComparisonMetric
  netProfit: PeriodComparisonMetric
}

export interface AdjustmentActivityRow {
  date: string
  category: string
  amount: number
}

export interface PracticeRevenueDashboardResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: PracticeRevenueSummary
  grossCollectionPeriodSummary: PeriodBucket[]
  netCollectionPeriodSummary: PeriodBucket[]
  adjustmentActivity: AdjustmentActivityRow[]
}

export const getPracticeRevenue = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<PracticeRevenueDashboardResult> =>
  axios
    .get<
      ApiResponse<PracticeRevenueDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/practice-revenue`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getPracticeRevenueOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<PracticeRevenueDashboardResult> =>
  axios
    .get<
      ApiResponse<PracticeRevenueDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/practice-revenue/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface TreatmentValueSummary {
  billed: PeriodComparisonMetric
  collected: PeriodComparisonMetric
  potential: PeriodComparisonMetric
}

export interface TreatmentValueByPeriodRow {
  period: string
  billed: number
  collected: number
  potential: number
}

export interface TreatmentValueDashboardResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  summary: TreatmentValueSummary
  byPeriod: TreatmentValueByPeriodRow[]
}

export const getTreatmentValue = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<TreatmentValueDashboardResult> =>
  axios
    .get<
      ApiResponse<TreatmentValueDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/treatment-value`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getTreatmentValueOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<TreatmentValueDashboardResult> =>
  axios
    .get<
      ApiResponse<TreatmentValueDashboardResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/treatment-value/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionByProvider = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionByProviderResult> =>
  axios
    .get<
      ApiResponse<ProductionByProviderResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production-by-provider`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionByProviderOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionByProviderResult> =>
  axios
    .get<
      ApiResponse<ProductionByProviderResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production-by-provider/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionByProcedure = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionByProcedureResult> =>
  axios
    .get<
      ApiResponse<ProductionByProcedureResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production-by-procedure`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionByProcedureOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionByProcedureResult> =>
  axios
    .get<
      ApiResponse<ProductionByProcedureResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production-by-procedure/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getARAging = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ARAgingDashboard> =>
  axios
    .get<
      ApiResponse<ARAgingDashboard>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/ar-aging`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getARAgingOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ARAgingDashboard> =>
  axios
    .get<
      ApiResponse<ARAgingDashboard>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/ar-aging/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface AROver90Summary {
  arOver90: PeriodComparisonMetric
  arOver90Percent: PeriodComparisonMetric
  arOver90AppointmentCount: PeriodComparisonMetric
}

export interface AROver90AppointmentRow {
  patientId: string
  patientName: string
  arType: ARAgingPayerType
  arOver90Balance: number
  daysPast90: number
  lastPaymentDate: string | null
}

export interface AROver90Result {
  asOfDate: string
  previousDate: string
  period: DashboardPeriod
  summary: AROver90Summary
  arOver90PeriodSummary: PeriodBucket[]
  arOver90PercentPeriodSummary: PeriodBucket[]
  appointments: AROver90AppointmentRow[]
}

export const getAROver90 = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<AROver90Result> =>
  axios
    .get<
      ApiResponse<AROver90Result>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/ar-over-90`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getAROver90Overview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<AROver90Result> =>
  axios
    .get<
      ApiResponse<AROver90Result>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/ar-over-90/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getOutstandingBalance = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<OutstandingBalanceDashboard> =>
  axios
    .get<
      ApiResponse<OutstandingBalanceDashboard>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/outstanding-balance`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getOutstandingBalanceOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<OutstandingBalanceDashboard> =>
  axios
    .get<
      ApiResponse<OutstandingBalanceDashboard>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/outstanding-balance/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export interface HygieneProductionVisitsResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: HygieneVisitRow[]
  count: number
}

export const getHygieneProduction = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<HygieneProductionResult> =>
  axios
    .get<
      ApiResponse<HygieneProductionResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/hygiene-production`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHygieneProductionVisits = (
  params: DashboardFinancePeriodParams & DashboardTableQueryParams,
  options?: { signal?: AbortSignal },
): Promise<HygieneProductionVisitsResult> =>
  axios
    .get<
      ApiResponse<HygieneProductionVisitsResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/hygiene-production/visits`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getHygieneProductionOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<HygieneProductionResult> =>
  axios
    .get<
      ApiResponse<HygieneProductionResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/hygiene-production/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionByDepartment = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionByDepartmentResult> =>
  axios
    .get<
      ApiResponse<ProductionByDepartmentResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production-by-department`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProductionByDepartmentOverview = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ProductionByDepartmentResult> =>
  axios
    .get<
      ApiResponse<ProductionByDepartmentResult>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/finance/production-by-department/overview`, { params, signal: options?.signal })
    .then((response) => response.data.data)
